// HiddenBlockCard — compact card for blocks in the Hidden zone of the canvas.
//
// Shows block name + ActionFlow type badges. Badge clicks navigate to flow editor.
// Does NOT render actual block UI (no BlockV2).

import { useMemo } from 'react'
import { detectLinkedFlows, navigateToFlow, ACTION_BADGE_MAP, type LinkedFlow } from '@/features/page-editor/services/flow-linker'
import { Link2, Plus } from 'lucide-react'
import type { Flow } from '@cogo/types-v2'

interface HiddenBlockCardProps {
  blockName: string
  displayName: string
  flows: Record<string, Flow>
}

export function HiddenBlockCard({ blockName, displayName, flows }: HiddenBlockCardProps) {
  const linkedFlows = useMemo(
    () => detectLinkedFlows(blockName, flows),
    [blockName, flows],
  )

  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm shadow-sm">
      {/* Block name */}
      <span className="font-medium text-card-foreground truncate min-w-0">
        {displayName}
      </span>

      {/* Flow badges or Create Flow */}
      <div className="ml-auto flex items-center gap-1.5 shrink-0">
        {linkedFlows.length > 0 ? (
          linkedFlows.map((lf) => {
            const badge = ACTION_BADGE_MAP[lf.actionType]
            return (
              <button
                key={`${lf.flowId}-${lf.actionType}`}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${badge?.color ?? 'bg-muted text-muted-foreground'} hover:opacity-80 transition-opacity cursor-pointer`}
                onClick={(e) => {
                  e.stopPropagation()
                  navigateToFlow(lf.flowName)
                }}
                title={`${lf.flowName} — click to open in flow editor`}
              >
                <Link2 className="h-3 w-3" />
                <span>{badge?.label ?? lf.actionType}</span>
                <span className="opacity-60">{lf.flowName}</span>
              </button>
            )
          })
        ) : (
          <button
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
            onClick={(e) => {
              e.stopPropagation()
              // TODO: Phase 288 will implement Create Flow wizard
            }}
          >
            <Plus className="h-3 w-3" />
            Create Flow
          </button>
        )}
      </div>
    </div>
  )
}
